import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Select } from '@/components/Select';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { COUNTRY_MAP } from '@/lib/countries';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getDomains } from '@/api/tracker-domains';
import {
  getGeoRouters, createGeoRouter, updateGeoRouter, deleteGeoRouter,
  type GeoRouter, type GeoRouterInput, type GeoRouterRule,
} from '@/api/tracker-geo-routers';
import type { TrackerCampaign } from '@/api/tracker-types';

const inputCls = 'block w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary';

interface RouterForm {
  name: string;
  domain_id: string;
  slug: string;
  rules: GeoRouterRule[];
  default_campaign_id: string;
  fallback_url: string;
  status: 'active' | 'paused';
}
const emptyForm: RouterForm = {
  name: '', domain_id: '', slug: '', rules: [{ campaign_id: '', countries: [] }],
  default_campaign_id: '', fallback_url: '', status: 'active',
};

// Campaigns here are mostly named after their geo ("Rate2Cash · BO · android"),
// so picking one pre-fills its countries; the field stays editable.
function countriesInName(name: string): string[] {
  const codes = (name.match(/\b[A-Z]{2}\b/g) || []).filter((c) => COUNTRY_MAP[c]);
  return [...new Set(codes)];
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-content-muted mb-1">
        {label}{required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-content-subtle">{hint}</p>}
    </div>
  );
}

export function TrackerGeoRouters() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<GeoRouter | 'new' | null>(null);
  const [form, setForm] = useState<RouterForm>(emptyForm);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const { data: routers = [], isLoading } = useQuery({ queryKey: ['tracker-geo-routers'], queryFn: getGeoRouters });
  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: domains = [] } = useQuery({ queryKey: ['tracker-domains'], queryFn: getDomains });

  const campaignById = useMemo(() => Object.fromEntries(campaigns.map((c) => [c.id, c])), [campaigns]);
  const hostById = useMemo(() => Object.fromEntries(domains.map((d) => [d.id, d.hostname])), [domains]);
  const campaignOptions = useMemo(
    () => campaigns
      .filter((c) => c.status === 'active')
      .map((c) => ({ value: c.id, label: `${c.name} — ${hostById[c.domain_id] ?? '?'}/${c.slug}` })),
    [campaigns, hostById],
  );
  const campaignName = (id: string | null) => {
    if (!id) return '—';
    const c: TrackerCampaign | undefined = campaignById[id];
    return c ? c.name : `(deleted) ${id.slice(0, 8)}…`;
  };
  const linkOf = (r: GeoRouter) => `https://${hostById[r.domain_id] ?? '?'}/${r.slug}`;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['tracker-geo-routers'] });
  const saveMut = useMutation({
    mutationFn: (data: GeoRouterInput) =>
      editing && editing !== 'new' ? updateGeoRouter(editing.id, data) : createGeoRouter(data),
    onSuccess: () => { invalidate(); setEditing(null); },
    onError: (e: Error) => setError(e.message),
  });
  const statusMut = useMutation({
    mutationFn: (r: GeoRouter) => updateGeoRouter(r.id, { status: r.status === 'active' ? 'paused' : 'active' }),
    onSuccess: invalidate,
    onError: (e: Error) => alert(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: deleteGeoRouter,
    onSuccess: invalidate,
    onError: (e: Error) => alert(e.message),
  });

  function open(r: GeoRouter | 'new') {
    setError('');
    setForm(r === 'new' ? emptyForm : {
      name: r.name, domain_id: r.domain_id, slug: r.slug,
      rules: r.rules.length ? r.rules : [{ campaign_id: '', countries: [] }],
      default_campaign_id: r.default_campaign_id ?? '', fallback_url: r.fallback_url ?? '', status: r.status,
    });
    setEditing(r);
  }

  function setRule(i: number, patch: Partial<GeoRouterRule>) {
    setForm((f) => ({ ...f, rules: f.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  }

  function pickCampaign(i: number, campaignId: string) {
    const rule = form.rules[i];
    const taken = new Set(form.rules.flatMap((r, j) => (j === i ? [] : r.countries)));
    const guessed = rule && rule.countries.length === 0
      ? countriesInName(campaignById[campaignId]?.name ?? '').filter((c) => !taken.has(c))
      : rule?.countries ?? [];
    setRule(i, { campaign_id: campaignId, countries: guessed });
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.domain_id) { setError('Name and domain are required'); return; }
    saveMut.mutate({
      name: form.name.trim(),
      domain_id: form.domain_id,
      slug: form.slug.trim() || undefined,
      rules: form.rules.filter((r) => r.campaign_id || r.countries.length),
      default_campaign_id: form.default_campaign_id || null,
      fallback_url: form.fallback_url.trim() || null,
      status: form.status,
    });
  }

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(text); setTimeout(() => setCopied(null), 1500); }
    catch { window.prompt('Copy:', text); }
  };

  const columns = [
    { key: 'name', label: 'Name', render: (r: GeoRouter) => <span className="font-medium">{r.name}</span> },
    {
      key: 'link', label: 'Link',
      render: (r: GeoRouter) => (
        <button onClick={(e) => { e.stopPropagation(); copy(linkOf(r)); }} className="text-primary text-xs underline text-left">
          {copied === linkOf(r) ? 'Copied' : linkOf(r)}
        </button>
      ),
    },
    {
      key: 'rules', label: 'Geo → campaign',
      render: (r: GeoRouter) => (
        <div className="text-xs space-y-0.5">
          {r.rules.map((rule) => (
            <div key={rule.campaign_id + rule.countries.join()}>
              <span className="font-mono">{rule.countries.join(', ')}</span> → {campaignName(rule.campaign_id)}
            </div>
          ))}
          <div className="text-content-subtle">
            Other → {r.default_campaign_id ? campaignName(r.default_campaign_id) : r.fallback_url || '404'}
          </div>
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (r: GeoRouter) => (
        <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'active' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>{r.status}</span>
      ),
    },
    {
      key: 'actions', label: '',
      render: (r: GeoRouter) => (
        <EditOnly>
          <div className="flex gap-2">
            <button onClick={(e) => { e.stopPropagation(); statusMut.mutate(r); }} className="text-xs text-primary hover:underline">
              {r.status === 'active' ? 'Pause' : 'Resume'}
            </button>
            <button onClick={(e) => { e.stopPropagation(); if (confirm(`Delete ${r.name}? The link stops working.`)) deleteMut.mutate(r.id); }} className="text-xs text-danger hover:underline">Delete</button>
          </div>
        </EditOnly>
      ),
    },
  ];

  const editingRouter = editing && editing !== 'new' ? editing : null;
  const testHost = hostById[form.domain_id];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Geo Routers</h1>
          <p className="text-sm text-content-subtle mt-1">
            One link for every ad campaign: the visitor is sent to the tracker campaign set for their country. Query parameters (sub1…) go along.
          </p>
        </div>
        <EditOnly><Button onClick={() => open('new')}>New geo router</Button></EditOnly>
      </div>

      <DataTable columns={columns} data={routers} loading={isLoading} keyExtractor={(r) => r.id} onRowClick={(r) => open(r)} />

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editingRouter ? editingRouter.name : 'New geo router'} size="lg">
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="p-3 bg-danger-soft text-sm text-danger rounded">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name" required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Propeller · Rate2Cash" />
            </Field>
            <Field label="Domain" required>
              <Select
                value={form.domain_id}
                onChange={(v) => setForm({ ...form, domain_id: v })}
                options={domains.filter((d) => d.status === 'active').map((d) => ({ value: d.id, label: d.hostname }))}
                placeholder="Pick a domain"
                searchable
              />
            </Field>
            <Field label="Slug" hint={editingRouter ? 'Changing it breaks the link already in the ad campaigns.' : 'Empty = random.'}>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} placeholder="auto" />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onChange={(v) => setForm({ ...form, status: v as 'active' | 'paused' })}
                options={[{ value: 'active', label: 'active' }, { value: 'paused', label: 'paused (link gives 404)' }]}
              />
            </Field>
          </div>

          <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
            <div>
              <div className="text-sm font-semibold text-content">Geo → campaign</div>
              <div className="text-xs text-content-subtle mt-0.5">A country can be in one rule only. If a rule's campaign is paused, its visitors go to the default.</div>
            </div>
            {form.rules.map((rule, i) => {
              const taken = form.rules.flatMap((r, j) => (j === i ? [] : r.countries));
              const allowed = Object.keys(COUNTRY_MAP).filter((c) => !taken.includes(c));
              return (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-start">
                  <Select value={rule.campaign_id} onChange={(v) => pickCampaign(i, v)} options={campaignOptions} placeholder="Campaign" searchable />
                  <CountryMultiSelect value={rule.countries} onChange={(cs) => setRule(i, { countries: cs })} restrictTo={allowed} />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, rules: f.rules.filter((_, j) => j !== i) }))}
                    className="text-xs text-danger hover:underline py-2"
                  >Remove</button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, rules: [...f.rules, { campaign_id: '', countries: [] }] }))}
              className="text-xs text-primary hover:underline"
            >+ Add rule</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Other countries → campaign" hint="Optional. Countries no rule names go here.">
              <Select
                value={form.default_campaign_id}
                onChange={(v) => setForm({ ...form, default_campaign_id: v })}
                options={[{ value: '', label: '— none —' }, ...campaignOptions]}
                searchable
              />
            </Field>
            <Field label="Fallback URL" hint="Optional. Used when no campaign takes the visitor; without it they get 404.">
              <input type="url" value={form.fallback_url} onChange={(e) => setForm({ ...form, fallback_url: e.target.value })} className={inputCls} placeholder="https://..." />
            </Field>
          </div>

          {editingRouter && testHost && (
            <div className="text-xs text-content-subtle bg-surface-muted p-2 rounded space-y-1">
              <div>Propeller Target URL: <span className="font-mono break-all">https://{testHost}/{editingRouter.slug}?sub1=$&#123;SUBID&#125;&amp;sub2=$&#123;ZONEID&#125;&amp;sub3=$&#123;CAMPAIGNID&#125;</span></div>
              <div>Test a geo: add the country code, e.g. <span className="font-mono">https://{testHost}/{editingRouter.slug}/{(form.rules[0]?.countries[0] || 'de').toLowerCase()}</span></div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <EditOnly><Button type="submit" loading={saveMut.isPending}>{editingRouter ? 'Save' : 'Create'}</Button></EditOnly>
          </div>
        </form>
      </Modal>
    </div>
  );
}
